import { AfterViewInit, Component, computed, DestroyRef, DOCUMENT, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IntlTelInputComponent } from 'intl-tel-input/angular';
import { FormElementDirective, injectNgControl } from '../form-element.directive';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, pairwise, startWith } from 'rxjs/operators';

// ** NOTE - the intl-tel-input library will override the initial value and state of the Reactive Form Control when passed in directly.
// ** This causes some false positives when it comes to validation and eventing.
// ** Handled by tracking library instantiation and manually triggering the value and errors for the Form Control

@Component({
  selector: 'topo-phone-input',
  hostDirectives: [FormElementDirective],
  imports: [IntlTelInputComponent, ReactiveFormsModule, TranslatePipe],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss'
})
export class PhoneInputComponent extends FormElementDirective implements AfterViewInit {
  private document = inject(DOCUMENT);
  protected ngControl = injectNgControl();
  private destroyRef = inject(DestroyRef);

  protected inputEl = viewChild('telInput', { read: IntlTelInputComponent });
  private iti = computed(() => this.inputEl()?.getInstance());

  onTouched = () => this.ngControl.control.markAsTouched();

  onDisabledChange = (disabled: boolean) => {
    this.iti()?.setDisabled(disabled);
  };

  private skipComponentWrite = true;

  writeValue(value: any): void {
    super.writeValue(value);

    if (!this.skipComponentWrite && !!this.iti()) {
      if (this.iti().getNumber() !== value) {
        this.iti().setNumber(value);
      }
    }
  }

  // events will fire from component even while initializing utils library
  // using this property to help filter events for trigger handlers
  private isInitialized = signal(false);
  private initialValue: string;

  dropdownContainerId = input<any>(null);

  // protected initOptions: any; // library does not export type for this.

  initOptions: any;

  protected readonly inputClassBase = computed(() => {
    const sizeClass = this.sizeClass();
    return `input is-fullwidth ${sizeClass}`;
  });

  isReady = false;

  dropdownContainer = computed(() => {
    const containerId = this.dropdownContainerId();

    if (!containerId) {
      return null;
    }

    return this.document.getElementById(containerId)?.querySelector('.modal-card-body') ?? null;
  });

  ngOnInit(): void {
    const customPlaceholder = !!this.placeholder() ? this.placeholder : null;

    this.initOptions = {
      separateDialCode: true,
      fixDropdownWidth: false,
      countryOrder: ['fr', 'gb', 'us'],
      initialCountry: 'auto',
      loadUtils: () => import('intl-tel-input/utils'),
      geoIpLookup: (success: Function, failure: Function) => {
        fetch('https://ipapi.co/json')
          .then(res => res.json())
          .then(data => success(data.country_code))
          .catch(() => success('us'));
      },
      countrySearch: false,
      strictMode: true,
      dropdownContainer: this.dropdownContainer(),
      customPlaceholder
    };

    this.isReady = true;

    // because Angular wipes out error states when changing from enabled/disabled states, need to revalidate
    // this specific scenario currently happens on COP RSD when switching to Edit Document mode
    const { control } = this.ngControl;
    control.statusChanges
      .pipe(
        startWith(control.status),
        pairwise(),
        filter(([prev, curr]) => prev !== curr),
        filter(([prev, curr]) => prev === 'DISABLED' || curr === 'DISABLED'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const phoneNumber = control.value === '' ? null : control.value;
        const isValid = phoneNumber === null && !control.hasValidator(Validators.required) ? true : !this.inputEl().validate(control);
        this.setValidity(isValid, 'class');
      });
  }

  ngAfterViewInit(): void {
    if (!!this.inputEl()?.getInstance()) {
      const itiOnLoad = this.iti().promise;

      const initialValue = this.ngControl.control.value;
      this.initialValue = initialValue === '' ? null : initialValue;

      // wait until utils library (used for number validation) is finished loading
      itiOnLoad
        .then(() => {
          this.isInitialized.set(true);
          this.iti().setNumber(this.initialValue);
          this.iti().setDisabled(this.ngControl.control.disabled);
          this.ngControl.valueAccessor = this;

          if (!!this.dropdownContainer()) {
            this.listenToContainerScroll();
          }

          this.applyLegacyStyling();
        })
        .catch(err => {
          console.error(err);
        });
    }
  }

  protected setValidity(isValid: boolean | null, source: string): void {
    if (!this.isInitialized()) {
      return;
    }

    const { control } = this.ngControl;

    if (source === 'template' && (control.value == null || control.value === '') && !control.hasValidator(Validators.required)) {
      control.setErrors(null);
      return;
    }

    // handle if Angular form control has errors from any Validators set from parent component (i.e. required, maxLength, etc.)
    const currentErrors = control.errors;

    if (typeof isValid === 'boolean' && !isValid) {
      control.setErrors({
        ...currentErrors,
        invalidPhone: 'true'
      });
    }
  }

  protected handleNumberChange(value: string | null): void {
    if (!this.isInitialized() || value === null) {
      return;
    }

    const phoneNumber = value === '' ? null : value;

    const { control } = this.ngControl;

    this.skipComponentWrite = true;
    control.patchValue(phoneNumber);
    this.skipComponentWrite = false;

    const isValid = phoneNumber === null && !control.hasValidator(Validators.required) ? true : !this.inputEl().validate(control);
    this.setValidity(isValid, 'class');

    if (control.touched && control.pristine && phoneNumber !== this.initialValue) {
      control.markAsDirty();
    }
  }

  protected handleCountryChange(): void {
    if (!this.isInitialized()) {
      return;
    }

    const { control } = this.ngControl;

    if (control.value?.length) {
      this.setValidity(!this.inputEl().validate(control), 'class');

      // replaces dash characters that remain when number is invalid after changing country.
      const val = control.value.replace(/[^\d\+]+/g, '').replace(/(.)\++/g, '$1');
      this.iti()?.setNumber(val);
    }
  }

  private listenToContainerScroll(): void {
    // There is no direct programmatic method in the plugin API to force-close the dropdown.
    // When using dropdownContainer, it uses position: fixed which causes it stay in place when scrolling the parent.
    // intl-tel-input library only will auto-close the country select dropdown on the window's scroll event;
    // this is a workaround to make that happen
    this.dropdownContainer().addEventListener('scroll', (e: Event) => {
      window.dispatchEvent(new Event('scroll'));
    });
  }

  private applyLegacyStyling(): void {
    const input = this.inputEl().getInput();

    const fontSize = window.getComputedStyle(input).getPropertyValue('font-size');

    const countryContainer = input.previousElementSibling;

    const countryButton = countryContainer.querySelector('button');
    const flag = countryButton.firstElementChild;
    const dialCode = countryButton.lastElementChild as HTMLDivElement;

    dialCode.style.fontSize = fontSize;

    flag.insertBefore(dialCode, flag.lastElementChild);

    const dropdownId = countryButton.getAttribute('aria-controls');

    const insertDivider = () => {
      const countryList = this.document.getElementById(dropdownId).firstChild as HTMLUListElement;
      const divider = this.document.createElement('li');
      divider.classList.add('iti__divider');
      divider.setAttribute('role', 'separator');
      divider.setAttribute('aria-disabled', 'true');
      divider.style.margin = '5px 0';
      divider.style.borderBottom = 'solid 1px var(--neutral-color-400)';

      countryList.insertBefore(divider, countryList.children[3]);
    };

    if (!!this.dropdownContainer()) {
      input.addEventListener('open:countrydropdown', insertDivider, { once: true });
    } else {
      insertDivider();
    }
  }
}
